import { useEffect, useState } from 'react'

export interface PriceRange {
  from: string
  to: string
}

export enum Bet {
  UP = 'UP',
  DOWN = 'DOWN',
}

export type BetResult = 'win' | 'lose' | null

export type DetailedBetResult = {
  setFirstPriceAfterBetting: (value: number | null) => void
  setLastPrice: (value: number | null) => void
  priceRange: PriceRange
  setBet: (value: Bet | null) => void
  betResult: BetResult
  setBetResult: (value: BetResult) => void
  percentageChange: string
}

const useDetailedBetResult = (): DetailedBetResult => {
  const [firstPriceAfterBetting, setFirstPriceAfterBetting] = useState<
    number | null
  >(null)
  const [lastPrice, setLastPrice] = useState<number | null>(null)
  const [priceRange, setPriceRange] = useState<PriceRange>({
    from: '',
    to: '',
  })
  const [bet, setBet] = useState<Bet | null>(null)
  const [betResult, setBetResult] = useState<BetResult>(null)
  const [percentageChange, setPercentageChange] = useState('')

  useEffect(() => {
    if (firstPriceAfterBetting !== null && lastPrice !== null && bet !== null) {
      const calculatePriceChange = () => {
        const priceChange = lastPrice - firstPriceAfterBetting
        const percentageChange = (priceChange / firstPriceAfterBetting) * 100
        return Math.abs(percentageChange).toFixed(4)
      }

      const determineBetResult = (
        betDirection: Bet,
        percentageChange: string
      ): { result: 'win' | 'lose'; change: string } => {
        if (firstPriceAfterBetting === lastPrice) {
          return { result: 'win', change: `0.00` }
        }
        if (betDirection === Bet.UP) {
          return firstPriceAfterBetting > lastPrice
            ? { result: 'win', change: `+${percentageChange}` }
            : { result: 'lose', change: `-${percentageChange}` }
        } else if (betDirection === Bet.DOWN) {
          return firstPriceAfterBetting < lastPrice
            ? { result: 'win', change: `-${percentageChange}` }
            : { result: 'lose', change: `+${percentageChange}` }
        }

        return { result: 'win', change: `0.00` }
      }

      const fixedPercentageChange = calculatePriceChange()
      const { result, change } = determineBetResult(bet, fixedPercentageChange)

      setPriceRange({
        from: lastPrice?.toFixed(2),
        to: firstPriceAfterBetting?.toFixed(2),
      })
      setBetResult(result)
      setPercentageChange(change)
    }
  }, [lastPrice, firstPriceAfterBetting, bet])

  return {
    setFirstPriceAfterBetting,
    setLastPrice,
    priceRange,
    setBet,
    betResult,
    setBetResult,
    percentageChange,
  }
}

export default useDetailedBetResult
